import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import LocationAndExpcept from "../../constants/LocationAndExpcept";
import { getAllLocations } from "../../utils/ApiCalls/Locations";
import {
  createMenubar,
  updateMenubar,
  deleteMenubarData,
  getMenubarLocationAndId,
} from "../../utils/ApiCalls/Menubar";
import { addlog } from "../../utils/ApiCalls/Logs";
import { checkFields } from "../../utils/Validation/checkFields";

const MenubarInput = ({ menubar }) => {
  const navigate = useNavigate();
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [locationOption, setLocationOption] = useState([]);
  const [displayLocation, setDisplayLocation] = useState([]);
  const [currentLocation, setCurrentLocation] = useState([]);
  const [menuName, setMenuName] = useState(menubar?.menuName || "");
  const [simplifiedChineseMenuName, setSimplifiedChineseMenuName] = useState(
    menubar?.simplifiedChineseMenuName || ""
  );
  const [traditionalChineseMenuName, setTraditionalChineseMenuName] = useState(
    menubar?.traditionalChineseMenuName || ""
  );
  const [bahasaMenuName, setBahasaMenuName] = useState(menubar?.bahasaMenuName || "");
  const [url, setUrl] = useState(menubar?.url ?? "");
  const [isActive, setIsActive] = useState(
    menubar?.is_active === undefined ? true : menubar.is_active === 1
  );
  const [submitBtnState, setSubmitBtnState] = useState(false);

  useEffect(() => {
    getOptionLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOptionLocation = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    let data = await getAllLocations();
    if (data.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      setLocationOption(data?.data || []);
      if (!menubar?.id) {
        const singapore = (data?.data || []).find(
          (item) => item.location_code === "SG"
        );
        if (singapore) setDisplayLocation([singapore.id]);
      }
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    if (menubar) {
      setMenuName(menubar.menuName || "");
      setSimplifiedChineseMenuName(menubar.simplifiedChineseMenuName || "");
      setTraditionalChineseMenuName(menubar.traditionalChineseMenuName || "");
      setBahasaMenuName(menubar.bahasaMenuName || "");
      setUrl(menubar.url ?? "");
      if (menubar.is_active !== undefined) {
        setIsActive(menubar.is_active === 1);
      }
      if (menubar.id) getLocationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menubar]);

  const getLocationData = async () => {
    let data = await getMenubarLocationAndId(menubar.url ?? "");
    if (data.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      setCurrentLocation(data || []);
      setDisplayLocation((data || []).map((item) => item.locationId));
    }
  };

  const handleDisplayLocationChange = (selectedOptions) => {
    setDisplayLocation(selectedOptions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const values = {
      menuName,
      simplifiedChineseMenuName,
      traditionalChineseMenuName,
      bahasaMenuName,
      url,
      is_active: isActive ? 1 : 0,
    };

    let valid = checkFields(
      values,
      "menubar",
      ["menuName", "simplifiedChineseMenuName", "traditionalChineseMenuName", "bahasaMenuName", "url"],
      {
        menuName: "Menu Name",
        simplifiedChineseMenuName: "Simplified Chinese Menu Name",
        traditionalChineseMenuName: "Traditional Chinese Menu Name",
        bahasaMenuName: "Bahasa Menu Name",
        url: "url",
      }
    );
    if (!valid && displayLocation.length === 0) {
      valid = "Error: The following fields are empty - location";
    }

    if (!valid) {
      const existingUrlLocations = await getMenubarLocationAndId(url);
      if (!existingUrlLocations?.error) {
        const currentIds = new Set(currentLocation.map((item) => item.id));
        const isDuplicate = (existingUrlLocations || []).some(
          (item) => !currentIds.has(item.id)
        );
        if (isDuplicate) {
          valid = "Error: This URL/Slug is already present";
        }
      }
    }

    if (valid) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: valid,
      });
      return;
    }

    setSubmitBtnState(true);
    dispatch({ type: "SET_LOADING", loading: true });
    try {
      await Promise.all(
        currentLocation.map(async (item) => {
          if (!displayLocation.includes(item.locationId)) {
            await deleteMenubarData(item.id);
          }
        })
      );

      await Promise.all(
        displayLocation.map(async (locationId) => {
          const existing = currentLocation.find(
            (item) => item.locationId === locationId
          );
          let data;
          if (existing) {
            data = await updateMenubar(existing.id, values);
          } else {
            data = await createMenubar({ ...values, locationId });
          }
          if (data?.error) throw new Error(data.error);
        })
      );

      addlog(
        menubar?.id ? "Menubar Updated" : "Menubar Created",
        "Menubar",
        { id: menubar?.id, menuName, url, locations: displayLocation },
        userDetails.id
      );

      navigate("/website/cms/homepage/menubar");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.message || "Unknown error occurred",
      });
    } finally {
      setSubmitBtnState(false);
      dispatch({ type: "SET_LOADING", loading: false });
    }
  };

  return (
    <div className="container-fluid">
      <div className="card">
        <form className="input_form" onSubmit={handleSubmit}>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-3 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={menuName}
                    onChange={(e) => setMenuName(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Simplified Chinese Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={simplifiedChineseMenuName}
                    onChange={(e) => setSimplifiedChineseMenuName(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Traditional Chinese Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={traditionalChineseMenuName}
                    onChange={(e) => setTraditionalChineseMenuName(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    Bahasa Menu Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    value={bahasaMenuName}
                    onChange={(e) => setBahasaMenuName(e.target.value)}
                  />
                </div>
              </div>
              <LocationAndExpcept
                nondisplay
                displayLocation={displayLocation}
                locationOption={locationOption}
                setDisplayLocation={setDisplayLocation}
                handleDisplayLocationChange={handleDisplayLocationChange}
              />
              <div className="w-100"></div>
              <div className="col-lg-3 col-md-12 col-sm-12 col-12">
                <div className="fieldrow">
                  <label className="f_label">
                    URL / Slug <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    className="f_input"
                    type="text"
                    placeholder="e.g. deals"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="iti_inner">
                  <div className="icheck-primary d-inline">
                    <input
                      type="checkbox"
                      id="menubarActiveCheck"
                      checked={isActive}
                      onChange={() => setIsActive(!isActive)}
                    />
                    <label htmlFor="menubarActiveCheck">
                      <span style={{ fontWeight: isActive ? 600 : 200 }}>
                        Active
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card-footer text-right">
            <button disabled={submitBtnState} className="btn main_submit">
              {menubar?.id ? "Update" : "Add"} Menu
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MenubarInput;
